import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, NavLink } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../hooks/useAuth";
import LogoutButton from "../api/logoutButton";
import ToastMessage from "../layout/ToastMessage";
import LogoutPopUp from "../layout/logoutpopup";
import logo from "../../assets/images/logo/logo.png";
import star from "../../assets/images/profile/starMemberBorder.png";
import default_man from "../../assets/images/profile/default-man.png";
import default_woman from "../../assets/images/profile/default-woman.png";
import AnimatedButton from "../animation/AnimatedButton";
import { mainURL } from "../../assets/constants/api";
import "./Header.css";

const Header = () => {
  const { isAuthenticated, userData, logout, token, role } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLogoutPopUp, setShowLogoutPopUp] = useState(false);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoutErrorMessage, setLogoutErrorMessage] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showTopBar, setShowTopBar] = useState(false);

  const navigate = useNavigate();

  const menuRef = useRef(null);
  const avatarRef = useRef(null);

  const navItems = [
    { path: "/", label: "الرئيسية" },
    { path: "/members", label: "الأعضاء" },
    { path: "/blog", label: "مدونة الزواج" },
    { path: "/success-story", label: "قصص نجاح" },
    { path: "/subscriptions", label: "باقات التميز" },
  ];

  const userOptions = [
    { path: "/my-profile", label: "حسابي" },
    { path: "/my-chats", label: "الرسائل" },
    { path: "/subscription-info", label: "معلومات الإشتراك" },
    { path: "/user-settings", label: "الإعدادات" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 200);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        avatarRef.current &&
        !avatarRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async (logoutState) => {
    setLoading(true);
    setLogoutErrorMessage(null);
    try {
      const response = await axios.post(
        `${mainURL}/web/auth/${logoutState}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        logout();
        setSuccess(true);
        setTimeout(() => navigate("/login"), 500);
      } else {
        setLogoutErrorMessage("حصل خطأ أثناء تسجيل الخروج أعد المحاولة لاحقاً");
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 500) {
        setLogoutErrorMessage("خطأ في الخادم");
      } else if (status === 401) {
        setLogoutErrorMessage("يرجى تسجيل الدخول مجدداً");
        navigate("/login");
      } else {
        setLogoutErrorMessage("حدث خطأ غير متوقع");
      }
    } finally {
      setLoading(false);
    }
  };

  const navLinkVariants = {
    hover: { scale: 1.05, y: -2 },
    tap: { scale: 0.95 },
    active: { color: "#FFC143" },
  };

  return (
    <header dir="rtl">
      {showLogoutPopUp && (
        <LogoutPopUp
          handleLogout={() =>
            handleLogout(checked ? "logout-all-devices" : "logout")
          }
          show={true}
          setShow={setShowLogoutPopUp}
          checked={checked}
          setChecked={setChecked}
        />
      )}

      {logoutErrorMessage && (
        <ToastMessage type="danger" show={true} message={logoutErrorMessage} />
      )}

      {success && (
        <ToastMessage
          type="success"
          show={true}
          message="تم تسجيل الخروج بنجاح"
        />
      )}

      <motion.nav
        className={`navbar navbar-custom py-3 ${
          isScrolled ? "fixed-top shadow-sm" : ""
        }`}
        animate={{
          backgroundColor: isScrolled ? "rgba(57, 25, 101, 0.95)" : "#391965",
        }}
        transition={{ duration: 0.3 }}
      >
        {/* Header Top Bar */}
        {(!isScrolled || showTopBar) && (
          <div
  className={`top-header ${
    showTopBar ? "d-flex" : "d-none d-lg-flex"
  } justify-content-between align-items-center px-2 px-md-3 px-lg-5 pb-2 pb-md-3`}
  style={{ width: "100%" }}
>
            <div className="right-section d-flex align-items-center gap-2">
              <i className="icofont-location-pin text-warning" />
              <span>المأذون الشرعي الجعفري د. علي أحمد باقر</span>
            </div>

            <div className="left-section d-flex align-items-center gap-2">
              <span>965-9723-3444+</span>
              <i className="icofont-ui-call text-warning" />
            </div>
          </div>
        )}

        <div className="container d-flex justify-content-between align-items-center">
          {/* Logo */}
          <div className="d-flex">
            <div
              className="d-lg-none text-white"
              onClick={() => setShowTopBar((prev) => !prev)}
            >
              <i className={`icofont-caret-${showTopBar ? "down" : "up"}`}></i>
            </div>
            <Link to="/">
              <motion.img
                src={logo}
                alt="Logo"
                className="header-logo"
                whileHover={{
                  rotate: [-2, 2, -2],
                  transition: { duration: 0.8 },
                }}
              />
            </Link>
          </div>

          {/* Nav items */}
          <div className="d-none d-lg-flex gap-5">
            {navItems.map((item) => (
              <motion.div key={item.path} whileHover="hover" whileTap="tap">
                <NavLink to={item.path}>
                  {({ isActive }) => (
                    <motion.span
                      variants={navLinkVariants}
                      animate={isActive ? "active" : ""}
                    >
                      {item.label}
                    </motion.span>
                  )}
                </NavLink>
              </motion.div>
            ))}
          </div>

          {/* User Section */}
          <div
            className="d-flex align-items-center gap-3"
            style={{ cursor: "pointer" }}
          >
            {isAuthenticated ? (
              <>
                <div className="d-flex flex-row align-items-center gap-3">
                  <div
                    ref={avatarRef}
                    className="d-flex flex-row align-items-center justify-content-between"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMenuOpen((prev) => !prev);
                    }}
                  >
                    <i
                      className={`icofont-caret-${
                        isMenuOpen ? "up" : "down"
                      } m-2`}
                    />

                    {userData?.profile && (
                      <div
                        className="text-center"
                        style={{ position: "relative", zIndex: 1 }}
                      >
                        <img
                          src={
                            userData.profile.profile_image ||
                            (userData.user_information?.gender === "ذكر"
                              ? default_man
                              : default_woman)
                          }
                          className="user-avatar"
                          style={{
                            transition: "transform 0.3s ease",
                            position: "relative",
                             width: '35px',
  height: '35px',
                          }}
                        />
                        {role === "star" && (
                          <img
                            src={star}
                            alt="star"
                            className="user-avatar"
                            style={{
                              position: "absolute",
                              top: "0px",
                              right: "0px",
                               width: '35px',
  height: '35px',
 

                              scale: 1.4,
                              zIndex: 0,
                            }}
                          />
                        )}
                        <small
                        className="text-center text-warning"
                          style={{
                            position: "absolute",
                            top: "35px",
                            right: "0",
                          
                            fontSize:"0.7rem"
   
                          }}
                        >
                          {userData?.user_information.user_name.slice(0,7)}
                        </small>
                      </div>
                    )}
                  </div>

                  <AnimatedButton>
                    <LogoutButton
                      loading={loading}
                      setShowLogoutPopUp={setShowLogoutPopUp}
                    />
                  </AnimatedButton>
                </div>
              </>
            ) : (
              <AnimatedButton>
                <NavLink to="/login" className="btn btn-outline-warning">
                  <i className="icofont-login me-2" />
                  دخول
                </NavLink>
              </AnimatedButton>
            )}
          </div>
        </div>

        {/* Dropdown */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              ref={menuRef}
              className="mobile-menu-overlay w-100"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="container">
                <div className="d-flex flex-column gap-3 p-3">
                  {userOptions.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="nav-link"
                      onClick={() => setIsMenuOpen(false)}
                      
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </header>
  );
};

export default Header;
